import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { Conversation, ConversationStatus } from "@zapir/shared";
import { AppBar } from "./AppBar";
import { Chips } from "./Chips";
import { ConversationRow } from "./ConversationRow";
import { ConversationListSkeleton } from "./Skeletons";
import { EmptyState } from "./EmptyState";
import { Banner } from "./Banner";
import { Icon } from "./Icon";
import { Avatar } from "./Avatar";
import { ConversationActionsSheet } from "./ConversationActionsSheet";
import { NewConversationSheet } from "./conversations/NewConversationSheet";
import {
  useCachedConversations,
  useConversations,
  usePinConversation,
  useSetRead,
  groupConversations,
  type ConversationFilter,
} from "@/hooks/useConversations";
import { useNotifications } from "@/hooks/useApp";
import { activeTenant, useConnection, useSession } from "@/lib/store";
import { asApiError } from "@/lib/errors";
import { formatRelative, plural } from "@/lib/format";

interface Props {
  activeId?: string;
}

const FILTERS: { value: ConversationFilter; label: string }[] = [
  { value: "todas", label: "Todas" },
  { value: "nao_lidas", label: "Não lidas" },
  { value: "minhas", label: "Minhas" },
  { value: "fixadas", label: "Fixadas" },
  { value: "sem_resposta", label: "Sem resposta" },
];

/**
 * Painel da lista de conversas (doc 09 §3.2): filtros, bloco FIXADAS no topo
 * com icone de pin, etiquetas, atribuicao, nao lidas e cache offline.
 */
export function ConversationsListPanel({ activeId }: Props) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const conn = useConnection((s) => s.conn);
  const me = useSession((s) => s.me);
  const tenant = activeTenant();
  const notifications = useNotifications();
  const [menuFor, setMenuFor] = useState<Conversation | null>(null);
  const pin = usePinConversation();
  const setRead = useSetRead();

  const [composeOpen, setComposeOpen] = useState(false);

  const filtro = (params.get("filtro") as ConversationFilter | null) ?? "todas";
  const etiqueta = params.get("etiqueta") ?? undefined;
  const assignee = params.get("assignee") ?? undefined;
  const status = (params.get("status") as ConversationStatus | null) ?? undefined;

  const query = useConversations({ filtro, etiqueta, assignee, status });
  const cached = useCachedConversations();

  const apiError = asApiError(query.error);
  const isOffline = conn === "offline" || apiError?.status === 0;
  const items: Conversation[] = useMemo(
    () => (query.items.length > 0 ? query.items : isOffline ? cached.items : []),
    [query.items, isOffline, cached.items],
  );
  const { pinned, others } = useMemo(() => groupConversations(items), [items]);

  const unreadTotal = items.reduce((sum, c) => sum + c.unreadCount, 0);
  const unreadNotifs = (notifications.data?.items ?? []).filter((n) => !n.readAt).length;

  const setFilter = (value: ConversationFilter) => {
    const next = new URLSearchParams(params);
    if (value === "todas") next.delete("filtro");
    else next.set("filtro", value);
    setParams(next, { replace: true });
  };

  const open = (conversation: Conversation) => {
    navigate(`/c/${conversation.id}`);
  };

  // Fixadas e "outras conversas" renderizam a MESMA linha; funcao simples (nao
  // um componente novo) para o React continuar reconciliando o mesmo tipo.
  const renderRow = (conversation: Conversation) => (
    <ConversationRow
      key={conversation.id}
      conversation={conversation}
      active={conversation.id === activeId}
      onOpen={() => open(conversation)}
      onMenu={() => setMenuFor(conversation)}
      onPin={() => pin.mutate({ id: conversation.id, pinned: !conversation.pinnedAt })}
      onToggleRead={() => setRead.mutate({ id: conversation.id, read: conversation.unreadCount > 0 })}
    />
  );

  const showSkeleton = query.isLoading && items.length === 0;

  return (
    <>
      <AppBar className="appbar-tg">
        <div className="appbar-main">
          <div className="min-w-0 flex-1">
            <h1 className="appbar-title">Conversas</h1>
            <button
              type="button"
              className="appbar-sub flex max-w-full items-center gap-1 text-left"
              onClick={() => navigate("/entrar/empresa")}
              aria-label={`Trocar de empresa (atual: ${tenant?.name ?? "Inbox"})`}
            >
              <span className="truncate">{tenant?.name ?? "Inbox"}</span>
              {isOffline ? <span className="shrink-0">· offline</span> : null}
              <Icon name="chevron" size={14} className="shrink-0 rotate-90" />
            </button>
          </div>
          <button type="button" className="icon-btn" aria-label="Nova conversa" onClick={() => setComposeOpen(true)}>
            <Icon name="plus" />
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Buscar conversas"
            onClick={() => navigate("/busca")}
          >
            <Icon name="busca" />
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label={`Notificações${unreadNotifs > 0 ? `, ${plural(unreadNotifs, "não lida", "não lidas")}` : ""}`}
            onClick={() => navigate("/notificacoes")}
          >
            <Icon name="sino" />
            {unreadNotifs > 0 ? <span className="badge-count">{unreadNotifs > 99 ? "99+" : unreadNotifs}</span> : null}
          </button>
          <button
            type="button"
            className="icon-btn"
            aria-label="Perfil e ajustes"
            onClick={() => navigate("/ajustes")}
          >
            <Avatar name={me?.name ?? "Usuário"} size={32} />
          </button>
        </div>
        <button
          type="button"
          className="tg-search"
          onClick={() => navigate("/busca")}
          aria-label="Pesquisar conversas e mensagens"
        >
          <Icon name="busca" size={20} className="shrink-0" />
          <span className="truncate">Pesquisar</span>
        </button>
      </AppBar>

      <Chips
        className="tg-filters"
        ariaLabel="Filtros da lista"
        value={filtro}
        onChange={setFilter}
        options={FILTERS.map((f) => ({ ...f, badge: f.value === "nao_lidas" ? unreadTotal : undefined }))}
      />

      {isOffline ? (
        <Banner tone="warn">
          <span className="dot offline" aria-hidden="true" />
          <div className="flex flex-col gap-[2px]">
            <span className="font-semibold">
              {cached.items.length > 0 ? "Sem conexão — mostrando conversas em cache" : "Sem conexão"}
            </span>
            <span className="text-[11px]">
              {cached.items.length > 0
                ? `Mensagens em leitura. Ações entram na fila e sincronizam ao reconectar.${cached.cachedAt ? ` Cache de ${formatRelative(new Date(cached.cachedAt).toISOString())}.` : ""}`
                : "Tente de novo quando a conexão voltar."}
            </span>
          </div>
        </Banner>
      ) : null}

      {apiError && !isOffline ? (
        <Banner tone="danger">
          <Icon name="warning" size={16} className="mt-[2px] shrink-0" />
          <div className="flex flex-col gap-1">
            <span className="font-bold">{apiError.copy}</span>
            <span className="text-[12px]">{apiError.action}</span>
            <span className="mono text-[10px] opacity-80">
              {apiError.status} · {apiError.code}
            </span>
            <button type="button" className="btn mt-1 min-h-[36px] self-start px-3 py-1 text-xs" onClick={() => void query.refetch()}>
              Tentar de novo
            </button>
          </div>
        </Banner>
      ) : null}

      {showSkeleton ? (
        <ConversationListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Icon name="conversas" size={34} />}
          title={filtro === "todas" ? "Nenhuma conversa por aqui" : "Nada neste filtro"}
          description={
            filtro === "todas"
              ? "Quando um cliente escrever, a conversa aparece aqui."
              : "Troque o filtro ou limpe as etiquetas para ver mais conversas."
          }
          action={
            filtro !== "todas" ? (
              <button type="button" className="btn" onClick={() => setFilter("todas")}>
                Ver todas
              </button>
            ) : null
          }
        />
      ) : (
        <div className="list" role="list" aria-busy={query.isFetching} aria-label="Conversas">
          {pinned.length > 0 ? (
            <>
              <div className="pin-group" role="presentation">
                <Icon name="pin" size={14} className="pin-glyph" />
                Fixadas
              </div>
              {pinned.map(renderRow)}
            </>
          ) : null}

          {pinned.length > 0 && others.length > 0 ? (
            <div className="pin-group" role="presentation">
              Outras conversas
            </div>
          ) : null}

          {others.map(renderRow)}

          {query.hasNextPage ? (
            <div className="p-4">
              <button
                type="button"
                className="btn block"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? "Carregando…" : "Carregar mais"}
              </button>
            </div>
          ) : (
            <p className="p-4 text-center text-[11px] muted">Fim da lista</p>
          )}
        </div>
      )}

      {menuFor ? (
        <ConversationActionsSheet
          conversation={menuFor}
          open
          onClose={() => setMenuFor(null)}
          onAddNote={() => navigate(`/c/${menuFor.id}`)}
        />
      ) : null}

      <NewConversationSheet open={composeOpen} onClose={() => setComposeOpen(false)} />
    </>
  );
}
